import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GeodataProcessType } from '../../../../../../typings/api/skymap/rest/v0/.common';
import {
  CreatePendingGeodataRequestBody,
  GeodataOptions,
  GetGeodataResponse,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { gsdToMeters } from '../../../../utils/proj4/units';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { useGsdUnit } from '../../../hooks/use-gsd-unit';
import { GeodataSetupContextType } from '../../../state/geodata-setup-state';
import { convertGcpRowsToGcpCollectionModel } from '../geodata-configuration/parse-gcp-rows';

export type CreateStepStatus = 'pending' | 'success' | 'error';

export type CreateGeodataStepParams = {
  projectId: string;
  geodataConfiguration: GeodataSetupContextType['geodataConfiguration'];
  selectedPackage: GeodataProcessType;
  addons?: GeodataSetupContextType['addons'];
  useInteractiveSetup: boolean;
  gcpFilename?: string;
  gcpPoints: GeodataSetupContextType['gcpPoints'];
};

export const useCreateGeodataStep = (
  projectId: string | undefined,
  context: GeodataSetupContextType | undefined,
) => {
  const { t } = useTranslation();
  const gsdUnit = useGsdUnit();
  const { buildErrorList, handleError, clearErrors } = useErrorHandling({
    displayNonApiErrors: false,
  });
  const [geodataId, setGeodataId] = useState<string | undefined>();
  const [createStepStatus, setCreateStepStatus] = useState<CreateStepStatus>('pending');
  const lastParamsRef = useRef<CreateGeodataStepParams | null>(null);
  const creationInitiatedRef = useRef(false);

  const buildRequestBody = useCallback(
    (params: CreateGeodataStepParams): CreatePendingGeodataRequestBody => {
      const options: GeodataOptions = {
        gsd: gsdToMeters(parseFloat(params.geodataConfiguration.gsd), gsdUnit),
        rollingShutter: params.geodataConfiguration.rollingShutter,
      };

      if (params.geodataConfiguration.objMaxTriangles) {
        options.objMaxTriangles = parseInt(params.geodataConfiguration.objMaxTriangles);
      }

      if (params.addons?.terrainModel?.enabled) {
        options.createTerrainModel = true;
      }

      if (params.addons?.filteredPointCloud?.enabled) {
        options.filteredPointCloud = {
          classes: params.addons?.filteredPointCloud?.classes ?? 'ALL',
          spacing: params.addons?.filteredPointCloud?.spacing ?? 0.02,
          format: params.addons?.filteredPointCloud?.format ?? 'DXF',
        };
      }

      if (params.addons?.gcpAdjustment?.enabled && !params.useInteractiveSetup) {
        options.gcpCorrectionJob = {
          comment: params.addons?.gcpAdjustment?.comment,
          selfReview: params.addons?.gcpAdjustment?.selfReview,
        };
      }

      const gcpCollection =
        convertGcpRowsToGcpCollectionModel(params.gcpFilename, params.gcpPoints) ?? undefined;

      return {
        projectId: params.projectId,
        name: params.geodataConfiguration.name,
        processType: params.selectedPackage,
        gcpCollection,
        options,
      };
    },
    [gsdUnit],
  );

  const mutation = useMutation<GetGeodataResponse, Error, CreatePendingGeodataRequestBody>({
    mutationFn: async (body: CreatePendingGeodataRequestBody) => {
      return SkyMapAxiosServiceFactory.instance
        .createGeodataServiceV0()
        .createPendingGeodata({ body });
    },
    onSuccess: (response) => {
      setGeodataId(response.data.id);
      setCreateStepStatus('success');
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: t('setupDialog.successfullyCreatedGeodata', { ns: 'cloudProcessing' }),
      });
    },
    onMutate: () => clearErrors(),
    onError: (error) => {
      setCreateStepStatus('error');
      handleError(error);
    },
    retry: false,
    networkMode: 'always',
  });

  const startCreation = useCallback(
    (params: CreateGeodataStepParams) => {
      lastParamsRef.current = params;
      const body = buildRequestBody(params);
      setCreateStepStatus('pending');
      mutation.mutate(body);
    },
    [buildRequestBody, mutation],
  );

  const retry = useCallback(() => {
    if (lastParamsRef.current) {
      startCreation(lastParamsRef.current);
    }
  }, [startCreation]);

  const errorList = useMemo(() => {
    return buildErrorList({ retryCallback: retry });
  }, [buildErrorList, retry]);

  // Auto-start creation when conditions are met
  useEffect(() => {
    if (!projectId || !context) {
      return;
    }

    // Prevent duplicate creation in React Strict Mode
    if (creationInitiatedRef.current || geodataId) {
      return;
    }

    // Step 1: Create pending geodata
    if (createStepStatus === 'pending') {
      creationInitiatedRef.current = true;
      const params: CreateGeodataStepParams = {
        projectId,
        geodataConfiguration: context.geodataConfiguration,
        selectedPackage: context.selectedPackage,
        addons: context.addons,
        useInteractiveSetup: context.useInteractiveSetup,
        gcpFilename: context.gcpFilename,
        gcpPoints: context.gcpPoints,
      };

      startCreation(params);
    }
  }, [projectId, context, createStepStatus, startCreation, geodataId]);

  return {
    retry,
    geodataId,
    createStepStatus,
    isPending: mutation.isPending,
    isError: mutation.isError,
    errorList,
  };
};
